import { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, FormControl, Box, CircularProgress } from '@mui/material';
import { searchAgencyOfficials } from '../networkRequest/officialsService';
import { OPERATION_AGENCY_OFFICIAL_ROLES } from '../constants';

export interface SearchableAgencyOfficialDropdownOption {
  id: number;
  first_name: string;
  last_name: string;
  img_url?: string;
  title?: string;
  rank?: string;
  badge_number?: string;
  agency_name?: string;
  assignment?: string;
}

interface SearchableAgencyOfficialDropdownProps {
  onSelect: (official: SearchableAgencyOfficialDropdownOption | null, assignment: string | null) => void;
  label?: string;
  accessToken: string;
  value: SearchableAgencyOfficialDropdownOption | null;
  onError: (error: string) => void;
  assignment?: string | null;
  hideAssignmentField?: boolean;
  onClear?: (id: number) => void;
}

const MIN_SEARCH_LENGTH = 3;
const SEARCH_LIMIT = 10;

const SearchableAgencyOfficialDropdown = ({
  onSelect,
  label = 'Official',
  accessToken,
  value,
  onError,
  assignment,
  hideAssignmentField = true,
  onClear = () => {},
}: SearchableAgencyOfficialDropdownProps) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchableAgencyOfficialDropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const lastQueryRef = useRef('');
  const [newAssignment, setNewAssignment] = useState<string | null>(assignment ?? null)
  // const [newOfficial, setNewOfficial] = useState <SearchableAgencyOfficialDropdownOption | null>(value ?? null)

  // Fetch officials when input length is a multiple of 3 and >= 3
  useEffect(() => {
    const fetchSearchResults = async (search: string) => {
      try {
        setLoading(true);
        lastQueryRef.current = search;
        const response = await searchAgencyOfficials({ accessToken, search, limit: SEARCH_LIMIT })
        if (response.data) {
          setSearchResults(response.data)
        } else if (response.error) {
          onError(response.error)
        } else {
          throw new Error("No valid search results returned")
        }
      } catch (error: any) {
        onError(error.message || error.toString())
      } finally {
        setLoading(false)
      }
    }

    const search = inputValue.trim();
    if (
      search.length >= MIN_SEARCH_LENGTH &&
      search.length % 3 === 0 &&
      search !== lastQueryRef.current
    ) {
      fetchSearchResults(search)
    }
    // If input is cleared or too short, clear searchResults
    if (search.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      lastQueryRef.current = '';
    }
  }, [inputValue, accessToken]);

  return (
    <FormControl fullWidth>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1
        }}
      >
        <Autocomplete
          options={searchResults}
          filterOptions={(options) => options}
          value={value}
          sx={{ flex: 1 }}
          getOptionLabel={(option: any) => {
            if (option.id === undefined) {
              return '';
            }
            if (!option.agency_name) {
              return `${option.first_name} ${option.last_name}`;
            }
            return `${option.first_name} ${option.last_name} (${option.agency_name})`;
          }}
          onChange={(_e, newValue: any, reason) => {
            // reset value on clear
            if (reason === 'clear' && value) {
              onClear(value.id);
            }

            if (newValue) {
              // setNewOfficial(newValue);
              onSelect(newValue, newAssignment);
              setInputValue(''); // Clear search after selection
            }
          }}
          onInputChange={(_e, newValue, reason) => {
            if (reason === 'input') setInputValue(newValue);
          }}
          loading={loading}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                size="medium"
                slotProps={{
                  ...params.slotProps,

                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  }
                }}
              />
            );
          }}
          renderOption={(props, option) =>
            {
            return <Box component="li" {...props} key={option.id}>
              <span>{`${option.first_name} ${option.last_name}`}</span>
              {option.agency_name &&
                <span style={{ color: '#888', marginLeft: 8 }}>
                  {option.agency_name}
                </span>
              }
            </Box>
            }
          }
        />

        {!hideAssignmentField && (
          <TextField
            label='Assignment'
            value={newAssignment === null ? OPERATION_AGENCY_OFFICIAL_ROLES.COMMAND_STAFF : newAssignment}
            onChange={(event) => {
              setNewAssignment(event.target.value)
              onSelect(value, event.target.value)
            }}
            sx={{ flex: 1 }}
          />
        )}
      </Box>
    </FormControl>
  );
};

export default SearchableAgencyOfficialDropdown;